import { useState, useEffect } from "react";
import { 
  Box, Heading, Flex, Text, Table, Thead, Tbody, Tr, Th, Td, Spinner, Divider, Grid, GridItem 
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import axios from "../../axiosConfig";
import { InputIcon, ProcessesIcon, ClimateCultureIcon } from "../../assets/icons";
import { useTranslation } from "react-i18next";
import ReportItemLevelModal from "./ReportItemLevelModal";
import { useUser } from "../../contexts/UserContext";
import ReportSurveyHeader from "./ReportSurveyHeader";

const DEC_HOUSES = 2;

const AreaReport = ({ areaKey, color }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { organizationSurveyId } = useParams();
  const [areaData, setAreaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedFactor, setSelectedFactor] = useState(null);
  const [selectedFactorId, setSelectedFactorId] = useState(null);
  const [selectedContext, setSelectedContext] = useState(null);
  const isTourUser = user?.user_type === 'tour';


  const iconMap = {
    input: <InputIcon />,
    processes: <ProcessesIcon />,
    climate_and_culture: <ClimateCultureIcon />,
  };

  useEffect(() => {
    const fetchAreaData = async () => {
      try {
        const response = await axios.get(`/survey_reports/${organizationSurveyId}/area_analysis?area=${areaKey}`);
        setAreaData(response.data);
      } catch (err) {
        setError(t("reports.area_report.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchAreaData();
  }, [organizationSurveyId, areaKey, t]);

  if (loading) return <Spinner size="xl" />;
  if (error) return <Text color="red.500">{error}</Text>;

  return (
    <Box>
      <ReportSurveyHeader survey={areaData} />

      <Flex align="center" mb={6}>
        {iconMap[areaKey] || <InputIcon />}
        <Heading as="h1" size="2xl" color={color} ml={2}>{t(areaData.area)}</Heading>
      </Flex>

      <Divider />

      <Grid templateColumns={{ base: "1fr", lg: "repeat(2, minmax(0, 1fr))" }} gap={6} mt={6} alignItems="start">
        {/* Table 1: Score per Page */}
        <GridItem>
          <Heading as="h3" size="lg">{t("reports.area_report.factor_analysis")}</Heading>
          <Table variant="striped" size="sm" mt={4}>
            <Thead>
              <Tr>
                <Th>{t("reports.area_report.table_headers.page")}</Th>
                <Th>{t("reports.area_report.table_headers.index")}</Th>
                {/* <Th>{t("reports.area_report.table_headers.benchmark_score")}</Th> */}
              </Tr>
            </Thead>
            <Tbody>
              {areaData.page_stats.map((page) => (
                <Tr key={page.page}>
                  <Td>{page.page}</Td>
                  <Td>{parseFloat(page.stability_index).toFixed(DEC_HOUSES)}</Td>
                  {/* <Td>{parseFloat(page.benchmark_stability_index).toFixed(DEC_HOUSES)}</Td> */}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </GridItem>

        {/* Table 2: Overall Role-Based Score */}
        <GridItem>
          <Heading as="h3" size="lg">{t("reports.area_report.team_analysis")}</Heading>
          <Table size="sm" variant="striped" mt={4} bgColor="white">
            <Thead>
              <Tr>
                <Th>{t("reports.area_report.table_headers.category")}</Th>
                <Th>{t("reports.area_report.table_headers.index")}</Th>
                {/* <Th>{t("reports.area_report.table_headers.benchmark_score")}</Th> */}
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>{t("reports.area_report.table_headers.total")}</Td>
                <Td>{parseFloat(areaData.overall_stats.total.stability_index).toFixed(DEC_HOUSES)}</Td>
                {/* <Td>{parseFloat(areaData.overall_stats.total.benchmark_stability_index).toFixed(DEC_HOUSES)}</Td> */}
              </Tr>
              {areaData.overall_stats.roles.map((role) => (
                <Tr key={role.role}>
                  <Td>{role.role}</Td>
                  <Td>{parseFloat(role.stability_index).toFixed(DEC_HOUSES)}</Td>
                  {/* <Td>{parseFloat(role.benchmark_stability_index).toFixed(DEC_HOUSES)}</Td> */}
                </Tr>
              ))}
              <Tr>
                <Td>{t("reports.area_report.table_headers.leadership")}</Td>
                <Td>{parseFloat(areaData.overall_stats.leadership.stability_index).toFixed(DEC_HOUSES)}</Td>
                {/* <Td>{parseFloat(areaData.overall_stats.leadership.benchmark_stability_index).toFixed(DEC_HOUSES)}</Td> */}
              </Tr>
              <Tr>
                <Td>{t("reports.area_report.table_headers.staff")}</Td>
                <Td>{parseFloat(areaData.overall_stats.staff.stability_index).toFixed(DEC_HOUSES)}</Td>
                {/* <Td>{parseFloat(areaData.overall_stats.staff.benchmark_stability_index).toFixed(DEC_HOUSES)}</Td> */}
              </Tr>
              <Tr>
                <Td>{t("reports.area_report.table_headers.difference")}</Td>
                <Td>{parseFloat(areaData.overall_stats.difference.stability_index).toFixed(DEC_HOUSES)}</Td>
                {/* <Td>-</Td> */}
              </Tr>
            </Tbody>
          </Table>
        </GridItem>
      </Grid>

      <Divider mt={6} borderColor="white" />

      <Heading as="h3" size="lg" mt={6}>{t("reports.area_report.team_analysis_per_factor")}</Heading>
      <Table size="sm" variant="striped" mt={4} bgColor="white">
        <Thead>
          <Tr>
            <Th>{t("reports.area_report.table_headers.role")}</Th>
            {areaData.page_stats.map((page) => (
              <Th key={page.page}>{page.page}</Th>
            ))}
          </Tr>
          {/* <Tr>
            <Th></Th>
            {areaData.page_stats.map(() => (
              <>
                <Th>{t("reports.area_report.table_headers.score")}</Th>
                <Th>{t("reports.area_report.table_headers.benchmark_score")}</Th>
              </>
            ))}
          </Tr> */}
        </Thead>
        <Tbody>
          {areaData.role_page_stats.map((role) => (
            <Tr key={role.role}>
              <Td>{t(`reports.area_report.table_headers.${role.role.toLowerCase()}`, role.role)}</Td>
              {role.pages.map((page) => (
                <>
                  <Td key={`${role.role}-${page.page}-stability_index`}>
                    { (role.role === 'Difference' || (isTourUser && ['processes', 'climate_and_culture'].includes(areaKey)))
                      ? ( parseFloat(page.stability_index).toFixed(DEC_HOUSES) )
                      : (
                        <Box
                          onClick={() => {
                            setSelectedTeam(role.role);
                            setSelectedFactorId(page.id);
                            setSelectedFactor(page.page);
                            setSelectedContext(t("reports.area_report.team_analysis_per_factor"));
                            setModalOpen(true);
                          }}
                          cursor="pointer"
                          textDecoration="underline"
                          _hover={{ color: "blue.600" }}
                        >
                          {parseFloat(page.stability_index).toFixed(DEC_HOUSES)}
                        </Box>
                      )
                    }
                  </Td>
                  {/* <Td key={`${role.role}-${page.page}-benchmark_stability_index`}>{role.role === 'Difference' ? '-' : parseFloat(page.benchmark_stability_index).toFixed(DEC_HOUSES)}</Td> */}
                </>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <ReportItemLevelModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        organizationSurveyId={organizationSurveyId}
        team={selectedTeam}
        factor={selectedFactor}
        factorId={selectedFactorId}
        context={selectedContext}
      />
    </Box>
  );
};

export default AreaReport;
